// Centralized error handling

import { HTTP_STATUS, ERROR_MESSAGES } from '../../constants/apiConstants';

class ErrorHandler {
  // Handle API errors
  handleError(error) {
    // Network error (no response from server)
    if (!error.response) {
      return {
        message: ERROR_MESSAGES.NETWORK_ERROR,
        status: null,
        data: null,
      };
    }

    const { status, data } = error.response;

    // Handle different HTTP status codes
    switch (status) {
      case HTTP_STATUS.BAD_REQUEST:
        return {
          message: data?.message || 'Invalid request',
          status,
          data: data?.errors || null,
        };

      case HTTP_STATUS.UNAUTHORIZED:
        return {
          message: data?.message || ERROR_MESSAGES.UNAUTHORIZED,
          status,
          data: null,
        };

      case HTTP_STATUS.FORBIDDEN:
        return {
          message: data?.message || ERROR_MESSAGES.FORBIDDEN,
          status,
          data: null,
        };

      case HTTP_STATUS.NOT_FOUND:
        return {
          message: data?.message || ERROR_MESSAGES.NOT_FOUND,
          status,
          data: null,
        };

      case HTTP_STATUS.CONFLICT:
        return {
          message: data?.message || 'Resource conflict',
          status,
          data: data?.errors || null,
        };

      case HTTP_STATUS.UNPROCESSABLE_ENTITY:
        return {
          message: data?.message || 'Validation error',
          status,
          data: data?.errors || null,
        };

      case HTTP_STATUS.INTERNAL_SERVER_ERROR:
        return {
          message: ERROR_MESSAGES.SERVER_ERROR,
          status,
          data: null,
        };

      case HTTP_STATUS.SERVICE_UNAVAILABLE:
        return {
          message: 'Service temporarily unavailable',
          status,
          data: null,
        };

      default:
        return {
          message: data?.message || ERROR_MESSAGES.UNKNOWN,
          status,
          data: data?.errors || null,
        };
    }
  }

  // Log error (can be extended to send to logging service)
  logError(error, context = '') {
    if (process.env.REACT_APP_ENABLE_LOGGING === 'true') {
      console.error(`[Error Handler] ${context}:`, error);
    }
  }

  // Check if error is authentication related
  isAuthError(error) {
    return (
      error.response?.status === HTTP_STATUS.UNAUTHORIZED ||
      error.response?.status === HTTP_STATUS.FORBIDDEN
    );
  }

  // Check if error is validation related
  isValidationError(error) {
    return (
      error.response?.status === HTTP_STATUS.BAD_REQUEST ||
      error.response?.status === HTTP_STATUS.UNPROCESSABLE_ENTITY
    );
  }

  // Format validation errors for form display
  formatValidationErrors(errors) {
    if (!errors) return {};
    
    // If errors is an array of objects with field and message
    if (Array.isArray(errors)) {
      return errors.reduce((acc, error) => {
        acc[error.field] = error.message;
        return acc;
      }, {});
    }
    
    // If errors is already an object
    return errors;
  }
}

// Export singleton instance
const errorHandler = new ErrorHandler();
export default errorHandler;
